import { z } from 'zod'

import { WorkbookPdfStatusSchema } from '~/@entities/Workbook'

const _localDatetime = z.iso.datetime({ local: true })

// 메시징 서버(messaging-api)는 Swagger 를 내지 않아 생성 타입이 없다. 아래 스키마는 2026-10-13
// mathFLAT/mathflat-messaging-server main 소스(NotificationDto·PayloadType)와 메인 서버 develop(cc5f60e17)의
// 알림 생산 코드로 확인한 모양이다. 알림 목록(GET /{partitionKey})과 SSE·MQTT 로 오는 실시간 알림은
// 메시징 서버가 저장한 같은 JSON 이다.

/***************** 알림 *****************/
// 메시징 서버가 생산자가 보낸 type 을 이 이름으로 정규화하고 모르는 값은 DEFAULT 로 바꾼다.
export const NOTIFICATION_PAYLOAD_TYPE = [
  'MATHFLAT_FINISH',
  'MATHFLAT_FILE_DOWNLOAD',
  'MATHFLAT_FILE_DOWNLOAD_API',
  'MATHFLAT_FILE_DOWNLOAD_ID_LIST',
  'MATHFLAT_ID_LIST',
  'MATHFLAT_REDIRECT',
  'MATHFLAT_MESSAGE',
  'MATHFLAT_SINGLE_REPORT_PREVIEW',
  'DEFAULT',
] as const
export const NotificationPayloadTypeSchema = z.enum(NOTIFICATION_PAYLOAD_TYPE)
export type NotificationPayloadType = z.infer<typeof NotificationPayloadTypeSchema>

// payload 는 생산자가 보낸 맵을 그대로 보존한다(선언하지 않은 키도 남는다). 생산자가 여러 서버라 메시징 서버는
// case·state 를 문자열로 받으므로 닫힌 목록으로 좁히지 않는다. id·ids·groupKey 처럼 payload 종류마다 다른 키는
// 생산자가 보낸 값이 그대로 실려 타입을 보장할 수 없어 선언하지 않는다.
export const NotificationPayloadSchema = z.looseObject({
  type: NotificationPayloadTypeSchema,
  title: z.string().nullish(),
  message: z.string().nullish(),
  case: z.string().nullish(),
  state: z.string().nullish(),
})
export type NotificationPayload = z.infer<typeof NotificationPayloadSchema>

// action 도 생산자가 정하는 문자열이다. data 는 CTA 마다 다른 문자열 맵(link·filePath 등)이다.
export const NotificationCtaSchema = z.object({
  action: z.string(),
  text: z.string().nullish(),
  data: z.record(z.string(), z.string()).nullish(),
})
export type NotificationCta = z.infer<typeof NotificationCtaSchema>

export const NotificationSchema = z.object({
  /** `{academyId}.{teacherId}` */
  partitionKey: z.string(),
  /** 생성 시각(epoch ms). 목록의 키이자 커서다 */
  sortKey: z.number(),
  createDateTime: _localDatetime,
  data: z
    .object({
      payload: NotificationPayloadSchema.nullable(),
      cta: z.array(NotificationCtaSchema),
    })
    .nullable(),
  read: z.boolean(),
  expireDateTime: _localDatetime.nullable(),
})
export type Notification = z.infer<typeof NotificationSchema>

export const NotificationPageSchema = z.object({
  result: z.array(NotificationSchema),
  hasBefore: z.boolean(),
})
export type NotificationPage = z.infer<typeof NotificationPageSchema>

/***************** 생성 상태 메시지 (메인 서버 → SSE·MQTT) *****************/
// 계약서·유형분석 보고서 PDF 상태. 어느 항목인지(id)는 싣지 않는다.
export const PdfStatusMessageSchema = z.object({ status: WorkbookPdfStatusSchema })
export type PdfStatusMessage = z.infer<typeof PdfStatusMessageSchema>

// 교재 PDF·커스텀 교재 썸네일 상태. 대상 교재 id 를 함께 싣는다.
export const StatusUpdateMessageSchema = z.object({
  id: z.number(),
  status: WorkbookPdfStatusSchema,
})
export type StatusUpdateMessage = z.infer<typeof StatusUpdateMessageSchema>

/***************** 푸시 알림 설정 (메인 서버) *****************/
export const PushNotificationSettingSchema = z.object({
  pushEnabled: z.boolean(),
})
export type PushNotificationSetting = z.infer<typeof PushNotificationSettingSchema>
